import { Rule, RuleObject } from 'antd/lib/form';
import { StoreValue } from 'antd/lib/form/interface';
import { get, isArray, isBoolean, isInteger, isNumber, isPlainObject } from 'lodash-es';
import { getParamFromUrl } from '@/infrastructure/history';
import { getProductId, isHarmonyOS } from '@/infrastructure/product-info';
import { Platform } from '@/infrastructure/utils/app';
import { ConfigTabType } from '@/presentation/pages/setting/app-deliver-config/api';
import { RecordType } from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import { IType } from '@/presentation/pages/setting/app-deliver-config/components/customize-item';
import { CUSTOMIZE_CONFIG } from '@/presentation/pages/setting/app-deliver-config/constant';


export enum TYPE {
  FLOAT = 'float',
  INT = 'int',
  LONG = 'long',
  BOOL = 'bool',
  STRING = 'string',
  UNKNOWN = 'unknown',
  OBJECT = 'object',
  ARRAY = 'array',
}

/**
 * 配置转换为表单
 * @param content 配置字符串
 * @param androidFullConfig 安卓全量配置
 * @param iosFullConfig ios全量配置
 * @param flutterFullConfig
 * @param windowsFullConfig
 * @param harmonyOSFullConfig 鸿蒙
 * @param fullConfigMode 全量配置中文名称
 * @returns sampleRatio：全局采样率，chunkConfig：其他配置项
 */
export const transform2Form = (
  content: string,
  androidFullConfig: RecordType,
  iosFullConfig: RecordType,
  flutterFullConfig: RecordType,
  windowsFullConfig: RecordType,
  harmonyOSFullConfig: RecordType,
  macFullConfig: RecordType,
  appleTVFullConfig: RecordType,
  fullConfigMode: RecordType,
) => {
  const { features = [], sample_ratio: sampleRatio, ...rest } = get(JSON.parse(content || '{}'), 'v7', {});
  const defaultRest = Object.keys(rest).map(key => ({ [key]: rest[key] })) ;
  let customizeCount = 0;
  const defaultFeature = features.map((item: RecordType) => {
    const { name, ...rest } = item;
    if (isCustomize(
      item, androidFullConfig, iosFullConfig, flutterFullConfig,
      windowsFullConfig, harmonyOSFullConfig, macFullConfig, appleTVFullConfig, fullConfigMode,
    )) {
      customizeCount  = customizeCount + 1;
      return { [`${CUSTOMIZE_CONFIG.name}-${customizeCount}`]: { name, ...rest } };
    }
    return { [name]: { name, ...rest } };
  });
  const chunkConfig =  [...defaultRest, ...defaultFeature];
  return {
    sampleRatio,
    chunkConfig,
  };
};

export const isSafeMode = (name: string) => name === 'safe_mode';

/**
 *
 * @param item 单个配置项
 * @param androidFullConfig 安卓全量配置
 * @param iosFullConfig ios全量配置
 * @param flutterFullConfig
 * @param windowsFullConfig
 * @param harmonyOSFullConfig 鸿蒙
 * @param fullConfigMode 全量配置中文名称
 * @returns 是否为自定义配置
 */
export const isCustomize = (
  item: RecordType,
  androidFullConfig: RecordType,
  iosFullConfig: RecordType,
  flutterFullConfig: RecordType,
  windowsFullConfig: RecordType,
  harmonyOSFullConfig: RecordType,
  macFullConfig: RecordType,
  appleTVFullConfig: RecordType,
  fullConfigMode: RecordType,
): boolean => {
  if (isSafeMode(item.name)) {
    return true;
  }

  const restConfigList = ['crash', 'atta'];
  if (fullConfigMode[item.name] || restConfigList.includes(item.name)) {
    return false;
  }

  const androidFeatures =  get(androidFullConfig, 'v7.features', []);
  const iosFeatures = get(iosFullConfig, 'v7.features', []);
  const flutterFeatures = get(flutterFullConfig, 'v7.features', []);
  const windowsFeatures = get(windowsFullConfig, 'v7.features', []);
  const harmonyOSFeatures = get(harmonyOSFullConfig, 'v7.features', []);
  const macFeatures = get(macFullConfig, 'v7.features', []);
  const appleTVFeatures = get(appleTVFullConfig, 'v7.features', []);
  const androidNames = androidFeatures.map((item: RecordType) => item.name);
  const iosNames = iosFeatures.map((item: RecordType) => item.name);
  const flutterNames = flutterFeatures.map((item: RecordType) => item.name);
  const windowsNames = windowsFeatures.map((item: RecordType) => item.name);
  const harmonyOSNames = harmonyOSFeatures.map((item: RecordType) => item.name);
  const macNames = macFeatures.map((item: RecordType) => item.name);
  const appleTVNames = appleTVFeatures.map((item: RecordType) => item.name);
  return !item.name || !androidNames.concat(
    iosNames, flutterNames,
    windowsNames, harmonyOSNames, macNames, appleTVNames,
  ).includes(item.name);
};

// 表单转换为配置
export const transform2Config = (content: string, addPlatform?: Platform): string => {
  const { configs, sampleRatio } = JSON.parse(content);
  const restConfigList = isHarmonyOS() || (addPlatform === Platform.HarmonyOS) ? ['crash', 'safe_mode'] : ['crash', 'atta', 'safe_mode'];
  const rest: RecordType = {};
  const features: RecordType[] = [];
  configs?.forEach((config: RecordType) => {
    const key = Object.keys(config)?.[0];
    const value = config[key];
    if (restConfigList.includes(key)) {
      rest[key] = value;
    } else {
      features.push(value);
    }
  });

  return JSON.stringify({
    v7: {
      sample_ratio: sampleRatio,
      features,
      ...rest,
    },
  });
};

const REGULA_FLOAT = /([-+]?[1-9]\.\d*[1-9])|([-+]?0\.\d*[1-9])|(^0$)|(^[-+]?[1-9]\d*$)/;
const REGULA_INT = /(^[-+]?[1-9]\d*$)|(^0$)/;
const REGULA_BOOL = /(^true$)|(^false$)/;

// 转换对应数据类型的值
export const transform2Value = (value: any, type?: string) => {
  try {
    if (type === TYPE.FLOAT) {
      const isValidFloat = REGULA_FLOAT.test(value);
      return isValidFloat ? Number.parseFloat(value) : value;
    }
    if (type === TYPE.STRING) {
      return value;
    }
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
};

// 值转换类型
export const transform2DataType = (value: any) => {
  if (isArray(value)) {
    return TYPE.ARRAY;
  }
  if (isPlainObject(value)) {
    return TYPE.OBJECT;
  }
  if (isNumber(value)) {
    return isInteger(value) ? TYPE.INT : TYPE.FLOAT;
  }
  if (isBoolean(value)) {
    return TYPE.BOOL;
  }
  return typeof value;
};

export const getSubConfigItemRules = (name: string, type: string): Rule[] => {
  const validatorField = (
    rule: RuleObject,
    value: StoreValue,
    callback: (error?: string) => void,
  ) => {
    const message = rule.message as string;
    const checkValue = (isValid: boolean) => (isValid ? callback() : callback(message));
    switch (type) {
      case TYPE.FLOAT:
        checkValue(REGULA_FLOAT.test(value));
        break;
      case TYPE.INT:
      case TYPE.LONG:
        checkValue(REGULA_INT.test(value));
        break;
      case TYPE.BOOL:
        checkValue(REGULA_BOOL.test(value));
        break;
      default:
        callback();
    }
  };

  return [
    { required: true, message: '配置属性不能为空' },
    { validator: validatorField, message: `${name}类型验证错误` },
  ];
};

// 自定义数据判断类型
export const customizeDataTransform = (value: any): IType => {
  if (isArray(value)) {
    return IType.ARRAY;
  }
  if (isPlainObject(value)) {
    return IType.OBJECT;
  }
  if (isNumber(value)) {
    return isInteger(value) ? IType.INT : IType.FLOAT;
  }
  if (isBoolean(value)) {
    return IType.BOOL;
  }
  return IType.STRING;
};

export const isLong = (str?: any) => {
  if (str === TYPE.LONG) {
    return TYPE.INT;
  }
  return str;
};

export const getConfigDocUrl = (type: ConfigTabType) => {
  switch (type) {
    case ConfigTabType.COMPLIANCE: {
      return `${location.origin}/v2/compliance/config/list?productId=${getProductId()}`;
    }
    default: {
      return undefined;
    }
  }
};

export const getConfigTab = (pathname: string) => {
  if (pathname.indexOf('/setting/compliance-app-deliver') > -1) {
    return ConfigTabType.COMPLIANCE;
  }
  if (pathname.indexOf('/config-manage') > -1 && getParamFromUrl('bar') === ConfigTabType.DEFAULT.toString()) {
    return ConfigTabType.DEFAULT;
  }
  if (pathname.indexOf('/config-manage') > -1 && getParamFromUrl('bar') === ConfigTabType.PLATFORM.toString()) {
    return ConfigTabType.PLATFORM;
  }
  return ConfigTabType.BUSINESS;
};

// 奇怪的前端兜底逻辑，后端无法处理，需要前端过滤为Android时的http配置项
export const filterConfigHttp = (config: string, type: ConfigTabType) => {
  try {
    const objectConfig = JSON.parse(config);
    const features = type === ConfigTabType.BUSINESS
      ? objectConfig?.v7?.features?.filter((item: Record<string, any>) => item.name !== 'http')
      : objectConfig?.v7?.features?.map((item: Record<string, any>) => {
        if (item.name === 'http') {
          const newItem = { ...item };
          delete newItem.error_portal_ratio;
          delete newItem.crash_portal_ratio;
          delete newItem.userinfo_ratio;
          return newItem;
        }
        return item;
      });
    const newObject = { ...objectConfig, v7: { ...objectConfig?.v7, features } };

    return JSON.stringify(newObject);
  } catch (e) {
    return config;
  }
};
